/**
 * CatalogueNumberInput — Input + inline error message + auto-normalization.
 *
 * Wraps the shadcn <Input> with:
 *   - onChange → auto-uppercases & strips spaces / normalises Æ→AE etc on the fly
 *     so the user sees the canonical value as they type
 *   - inline error message below the field when invalid
 *   - bubbles the validation state up via `onValidityChange(isValid)` so the
 *     parent form can disable submit
 *
 * Usage:
 *   <CatalogueNumberInput
 *       value={catalogueNumber}
 *       onChange={setCatalogueNumber}
 *       onValidityChange={setCatalogueValid}
 *       placeholder="CAT-001"
 *       data-testid="catalogue-number-input"
 *   />
 */
import React, { useEffect, useState } from 'react';
import { Input } from './ui/input';
import { AlertCircle } from 'lucide-react';
import { normalizeCatalogueNumber, validateCatalogueNumber, CATALOGUE_MAX_LENGTH } from '../lib/catalogueValidation';

export default function CatalogueNumberInput({
    value,
    onChange,
    onValidityChange,
    placeholder = 'CAT-001',
    className = 'h-9',
    label,
    required = false,
    disabled = false,
    'data-testid': dataTestId = 'catalogue-number-input',
}) {
    const [error, setError] = useState(null);

    // Re-evaluate validity whenever the externally-controlled value changes
    /* eslint-disable */
    useEffect(() => {
        const { error: e } = validateCatalogueNumber(value);
        if (required && !value) {
            // Required validity still gates submission, but we deliberately
            // do NOT surface an inline "Catalogue number is required" error —
            // the asterisk + helper text already communicate the requirement.
            setError(null);
            onValidityChange?.(false);
        } else {
            setError(e);
            onValidityChange?.(!e);
        }
    }, [value, required, onValidityChange]);
    /* eslint-enable */

    const handleChange = (raw) => {
        // Live-normalise as the user types: uppercase, transliterate Æ/Ø/Å, strip
        // whitespace. Empty string is allowed (so the user can clear the field).
        const normalised = normalizeCatalogueNumber(raw);
        onChange(normalised ?? '');
    };

    return (
        <div className="space-y-1">
            {label && (
                <label className="text-xs font-medium text-slate-500 block">
                    {label}{required && ' *'}
                </label>
            )}
            <Input
                value={value || ''}
                onChange={(e) => handleChange(e.target.value)}
                placeholder={placeholder}
                className={`${className} ${error ? 'border-red-400 focus-visible:ring-red-200' : ''}`}
                maxLength={CATALOGUE_MAX_LENGTH}
                disabled={disabled}
                data-testid={dataTestId}
                aria-invalid={!!error}
                aria-describedby={error ? `${dataTestId}-error` : undefined}
            />
            {error && (
                <p
                    id={`${dataTestId}-error`}
                    className="text-xs text-red-600 flex items-start gap-1"
                    data-testid={`${dataTestId}-error`}
                >
                    <AlertCircle className="h-3 w-3 mt-0.5 flex-shrink-0" />
                    <span>{error}</span>
                </p>
            )}
        </div>
    );
}
